import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot} from '@angular/router';
import {Store} from '@ngrx/store';
import {Observable, combineLatest} from 'rxjs';
import {map} from 'rxjs/operators';
import {UserRole} from '@graphql/generated';
import {selectUserRole, selectUserTokenRaw, selectUserRefreshTokenRaw} from '@shared/store/auth/auth.selector';
import {ImpersonationService} from '@shared/services/impersonation.service';
import {TokenStorageService} from '@shared/services/token-storage.service';

@Injectable({
  providedIn: 'root',
})
export class RolePermissionGuard implements CanActivate {
  constructor(
    private router: Router,
    private store: Store,
    private impersonationService: ImpersonationService,
    private tokenStorageService: TokenStorageService
  ) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return combineLatest([
      this.store.select(selectUserRole),
      this.store.select(selectUserTokenRaw),
      this.store.select(selectUserRefreshTokenRaw)
    ]).pipe(
      map(([userRole, userTokenRaw, userRefreshRaw]) => {
        const url = state.url || '';
        // Trigger potential impersonation handoff restoration by fetching token first
        const hasToken = !!this.tokenStorageService.getCurrentToken();
        let inImpersonation = this.tokenStorageService.isImpersonationMode() || !!this.tokenStorageService.getImpersonatedUserTokens();
        const storage = this.tokenStorageService.isImpersonationMode() ? sessionStorage : localStorage;
        const hasOrgId = !!storage.getItem('orgId');

        if (url.startsWith('/portal')) {
          // If session impersonation isn’t set yet but NgRx has userToken (rehydrated), set it now
          if (!inImpersonation && userTokenRaw) {
            this.tokenStorageService.setImpersonatedUserTokens(userTokenRaw, userRefreshRaw || userTokenRaw);
            inImpersonation = true;
          }

          // Allow portal only for impersonated session or client role
          if (inImpersonation || userRole === UserRole.C) {
            return true;
          }

          // If impersonated tokens exist (e.g., restored via handoff), enable impersonation and allow
          const hasUserToken = !!this.tokenStorageService.getImpersonatedUserTokens();
          if (hasUserToken) {
            this.tokenStorageService.setImpersonationMode(true);
            return true;
          }

          // Otherwise, deny access for admins/supports opening portal without impersonation
          this.router.navigate(['/permission-denied']);
          return false;
        }

        if (!userRole && !hasToken) {
          this.router.navigate(['/auth/login'], { queryParams: { nextTo: state.url }, queryParamsHandling: 'merge' });
          return false;
        }

        const requiredRoles: UserRole[] = route.data['roles'] || [];

        if (requiredRoles.length > 0) {
          // Normal role checking
          if (requiredRoles.includes(userRole)) {
            return true;
          } else {
            this.router.navigate(['permission-denied']);
            return false;
          }
        }

        // Special handling for root path ('')
        if (url === '/') {
          this.redirectToModuleHome(userRole);
          return false;
        }

        return true; // Allow access if no roles are specified
      })
    );
  }

  private redirectToModuleHome(userRole: UserRole): void {
    // Check if we're in impersonated session for root redirect
    const hasUserToken = !!this.tokenStorageService.getImpersonatedUserTokens();

    if (this.impersonationService.isImpersonating() || hasUserToken) {
      // During impersonation, redirect to portal
      this.router.navigate(['/portal']);
    } else {
      // Normal redirect based on user role
      if (userRole === UserRole.A || userRole === UserRole.S) {
        this.router.navigate(['/admin']);
      } else if (userRole === UserRole.C) {
        this.router.navigate(['/portal']);
      } else {
        this.router.navigate(['/auth/login']);
      }
    }
  }
}
